/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2024, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import { Component, Inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HttpClient } from '@angular/common/http';

/**
 * Component which handles inviting multiple users to a group.
 */
@Component({
  moduleId: module.id,
  selector: 'bulb-group-invite-dialog',
  templateUrl: './group-invite-dialog.component.html',
  standalone: false,
})
export class GroupInviteDialogComponent {

  invitedUsers : any = [];

  groupId : string;

  /**
   * Default Constructor.
   *
   * @param dialogRef Reference to the open dialog.
   * @param dynamicConfig Dialog configuration.
   * @param http Http client.
   * @param state
   */
  constructor(
    public dialogRef : DynamicDialogRef,
    public dynamicConfig: DynamicDialogConfig,
    private http: HttpClient,
    @Inject('$state') private state,
  ) {
    // @ts-ignore
    this.groupId = this.dynamicConfig.groupId;

    // @ts-ignore
    for (let i = 0; i < dynamicConfig.invitedUsers.length; i += 1) {
      this.invitedUsers.push({
        // @ts-ignore
        user: dynamicConfig.invitedUsers[i],
        requestSent: false,
        createdSuccessfully: false,
      });
    }
  }

  /**
   * Removes the invited user from the list to invite.
   */
  deleteUser(i : number) : void {
    this.invitedUsers.splice(i, 1);
  }

  /**
   * Adds an empty user object to the invitedUser list.
   */
  addUser() : void {
    this.invitedUsers.push({
      user: '',
      requestSent: false,
      createdSuccessfully: false,
    });
  }

  /**
   * Track by function to keep focus on the proper input.
   *
   * @param i index of the input field.
   */
  // eslint-disable-next-line class-methods-use-this
  trackBy(i) : void {
    return i;
  }

  /**
   * Handles submitting the invitedUser requests, the responses, and updating the state of the modal after the post.
   */
  submit() : void {
    // Remove empty fields.
    this.invitedUsers = this.invitedUsers.filter((user) => user.user && user.user.trim() !== '');

    for (let i = 0; i < this.invitedUsers.length; i += 1) {
      this.inviteMember(this.invitedUsers[i].user).then((response) => {
        // @ts-ignore
        this.dynamicConfig.members.push(response);
        this.invitedUsers[i].createdSuccessfully = true;
      }).catch((err) => {
        // This error indicates the user was already a member of the group, so we count it as a success.
        if (err.status === 400 && err.error.developerMessage.startsWith('No source object')) {
          this.invitedUsers[i].createdSuccessfully = true;
        }
      }).finally(() => {
        this.invitedUsers[i].requestSent = true;

        // Remove the successfully created user from the list after all requests have been sent and a short delay.
        if (this.invitedUsers.length - 1 === i) {
          let j = this.invitedUsers.length;
          setTimeout(() => {
            while (j > 0) {
              j -= 1;
              if (this.invitedUsers[j].createdSuccessfully) {
                this.invitedUsers.splice(j, 1);
              }
            }
            // Close the modal if all users are added successfully.
            if (this.invitedUsers.length === 0) {
              this.continue();
            }
          }, 1750);
        }
      });
    }
  }

  /**
   * The POST call to invite a member by username or email.
   */
  inviteMember(emailOrUsername) : Promise < any > {
    return this.http.post(`groups/${this.groupId}/members`, { emailOrUsername }).toPromise();
  }

  /**
   * Used to properly close the dialog in angular js.
   */
  continue() : void {
    this.dialogRef.close();
    // @ts-ignore
    if (this.dynamicConfig.runningInAngularJS) {
      /*
      Important Note:
      This is needed to remove primengs dynamic dialog from the DOM. For some reason, the component that
      registers the initial opening of the dialog can't seem to close it. This only happens in our AngularJS
      framework.
      */
      // @ts-ignore
      jQuery('p-dynamicdialog').remove();
    }
  }
}
